import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { format as formatDate, isBefore, parse } from "date-fns";

import { useObjectMemo, useReferenceTo } from "../../../hooks";
import { useFeature } from "../features";
import {
  useBAccount,
  recipients,
  useInstitution,
  achCompanies,
  holidays,
  useIsBusinessUser,
  limits,
  useAccounts,
  achDualApprovalRequired,
  dualApprovalRequired,
  useUser,
} from "../../entities";
import { useLibrary } from "../../../providers";
import filters from "../../../../filters";
import { getTotalCountFromRecurrenceString } from "../../../../Recurrence";
import OrganizationUser from "../../../../OrganizationUser";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore-error TS7016
import Organization from "../../../../Organization";
import { useFetchOrganizationUsers } from "../../entities/organizations";
import {
  useCheckReceiveInstantEligibility,
  useGetInstantOrSameDayFees,
  calculateInstantOrSameDayFee,
} from "../transfers";

import {
  ACHPaymentForm,
  type ACHDeliverySpeed,
  type ACHPaymentFormType,
  getAchScheduledPaymentDetailsSchema,
  getAchScheduledAdditionalDetailsSchema,
} from "./ACHPayment.form";
import { useManageRecipientPermission } from "./permissions";
import {
  formatValuesForRecurrenceString,
  getIsOneTimePaymentForToday,
  getReadableTimeFromCutoff,
  getIsPastACHCutoff,
  addBusinessDays,
  getNextBusinessDay,
  getRecommendedInvoicePaySpeed,
  getStartDate,
  isExpeditedACHDeliverySpeed,
  parseACHStartDate,
  type InvoicePayRecommendedSpeed,
} from "./utils";

import type { CollectPaymentFormType } from "./CollectPayment.form";
import type { ReactLocalization } from "@fluent/react";

export const useMappedEntryDescription = () => {
  const { features } = useFeature();
  const entryDescriptionOptions =
    features.allowed_ach_payment_entry_descriptions || [];

  const toSentenceCase = (value: string) => {
    return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const mappedOptions = entryDescriptionOptions.map((option) => ({
    display: toSentenceCase(option),
    value: option,
  }));

  const getSelectedEntryDescription = (value: string) =>
    mappedOptions.find((opt) => opt.value === value)?.display || value;

  return { mappedOptions, getSelectedEntryDescription };
};

export const useSECCodes = () => {
  const { features } = useFeature();
  return features.allowed_std_ent_cls_codes || [];
};

const DEFAULT_SEC_CODE = "CCD";
const DEFAULT_ENTRY_DESC_VALUE = "VENDOR PMT";

// both lists come from free-form institution config, so compare on a
// normalized value rather than trusting the stored casing/whitespace
const normalizeAllowedValue = (value: string) => value.trim().toUpperCase();

export const getDefaultAchPaymentSecCode = (
  secCodes: string[],
): string | undefined =>
  secCodes.find((code) => normalizeAllowedValue(code) === DEFAULT_SEC_CODE);

export const getDefaultAchPaymentEntryDesc = (
  entryDescriptions: string[],
): string | undefined =>
  entryDescriptions.find(
    (value) => normalizeAllowedValue(value) === DEFAULT_ENTRY_DESC_VALUE,
  );

export const useAchPaymentSettingsDefaults = () => {
  const secCodes = useSECCodes();
  const { mappedOptions } = useMappedEntryDescription();

  const defaultSecCode = useMemo(
    () => getDefaultAchPaymentSecCode(secCodes),
    [secCodes],
  );
  const defaultEntryDesc = useMemo(
    () =>
      getDefaultAchPaymentEntryDesc(mappedOptions.map(({ value }) => value)),
    [mappedOptions],
  );

  return useObjectMemo({
    defaultSecCode,
    defaultEntryDesc,
    hasAdvancedDetailDefaults: Boolean(defaultSecCode && defaultEntryDesc),
  });
};

export const useApplyAchPaymentSettingsDefaults = () => {
  const form = ACHPaymentForm.useForm();
  const { values, setFieldValue } = form;
  const { defaultSecCode, defaultEntryDesc } = useAchPaymentSettingsDefaults();
  const hasAppliedSecCodeDefault = useRef(false);
  const hasAppliedEntryDescDefault = useRef(false);

  // note: defaults are applied without re-validating. anything that validates
  // the form while these required fields are still empty (e.g. prefilling the
  // amount) leaves a stale "Required" error in `form.errors` that applying the
  // default does not clear, so read them through `touched` rather than trusting
  // `errors`/`isValid` directly.

  useEffect(() => {
    if (values.secCode) {
      hasAppliedSecCodeDefault.current = true;
      return;
    }

    if (!defaultSecCode || hasAppliedSecCodeDefault.current) {
      return;
    }

    hasAppliedSecCodeDefault.current = true;
    setFieldValue("secCode", defaultSecCode, false);
  }, [defaultSecCode, setFieldValue, values.secCode]);

  useEffect(() => {
    if (values.entryDesc) {
      hasAppliedEntryDescDefault.current = true;
      return;
    }

    if (!defaultEntryDesc || hasAppliedEntryDescDefault.current) {
      return;
    }

    hasAppliedEntryDescDefault.current = true;
    setFieldValue("entryDesc", defaultEntryDesc, false);
  }, [defaultEntryDesc, setFieldValue, values.entryDesc]);
};

export const useShouldShowSingleACHPaymentFlow = () => {
  const { features } = useFeature();

  return !!(features.ach_payments || features.ach_payments_collect);
};

export const useIsDebitACHPayment = () => {
  const {
    values: { transactionType },
  } = ACHPaymentForm.useForm();

  return transactionType === "debit";
};

// whether single-payment expedited delivery is available
export const useIsSingleExpediteEnabled = () => {
  const { features } = useFeature();

  return Boolean(features.single_expedite);
};

// whether the user can choose an expedited single ACH payment option
export const useIsSingleExpediteAvailable = () => {
  const { features } = useFeature();
  const isSingleExpediteEnabled = useIsSingleExpediteEnabled();

  return Boolean(
    isSingleExpediteEnabled && (features.same_day_ach || features.fednow_send),
  );
};

// whether the current payment is using instant delivery
// reflects the user's selection only — callers are responsible for gating on
// the feature that made the option selectable in the first place
export const useIsInstantACHDelivery = () => {
  const {
    values: { deliverySpeed },
  } = ACHPaymentForm.useForm();

  return deliverySpeed === "instant";
};

// whether the current in-progress payment is using same-day delivery
// similar to hook above, reflects the user's selection only — callers are responsible for feature gating
export const useIsSameDayACHDelivery = () => {
  const {
    values: { deliverySpeed },
  } = ACHPaymentForm.useForm();

  return deliverySpeed === "sameDay";
};

// whether the current in-progress payment uses an expedited delivery speed
export const useIsExpeditedACHDelivery = () => {
  const isInstantDelivery = useIsInstantACHDelivery();
  const isSameDayDelivery = useIsSameDayACHDelivery();

  return isInstantDelivery || isSameDayDelivery;
};

export type ACHArrivalEstimateType =
  | { kind: "dualApproval" }
  | { kind: "instant" }
  | { kind: "sameDay"; cutoffTime: string }
  // same-day payment placed after the cut-off; settles next business day
  | { kind: "sameDayCutoffPassed"; cutoffTime: string }
  | { kind: "standard"; arrivalDate: string };

type FeeQueryState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; fee: API.InstantFeesResponse };

const LOADING_FEE_STATE: FeeQueryState = { status: "loading" };

// returns null when the single_expedite flag is off
export const useACHArrivalEstimate = (): ACHArrivalEstimateType | null => {
  const isSingleExpediteEnabled = useIsSingleExpediteEnabled();
  const isDualApprovalRequired = useSelector(achDualApprovalRequired);
  const institution = useInstitution();
  const holidayDates = holidays.useHolidays();
  const {
    values: { deliverySpeed, startDate },
  } = ACHPaymentForm.useForm();

  return useMemo<ACHArrivalEstimateType | null>(() => {
    if (!isSingleExpediteEnabled) {
      return null;
    }

    if (isDualApprovalRequired) {
      return { kind: "dualApproval" };
    }

    if (deliverySpeed === "instant") {
      return { kind: "instant" };
    }

    if (deliverySpeed === "sameDay") {
      const cutoffTime = getReadableTimeFromCutoff(
        institution?.ach_next_cutoff,
      );
      if (!cutoffTime) {
        return null;
      }
      // once the cut-off has passed, a same-day payment settles the next
      // business day instead of "arriving by" the cut-off time
      return getIsPastACHCutoff(institution?.ach_next_cutoff)
        ? { kind: "sameDayCutoffPassed", cutoffTime }
        : { kind: "sameDay", cutoffTime };
    }

    // standard ACH arrives one business day after it is processed (the send
    // date, or the next business day if it falls on a weekend/holiday).
    // startDate may be "MM/dd/yyyy" (web) or "yyyy-MM-dd" (mobile)
    const sendDate = ["MM/dd/yyyy", "yyyy-MM-dd"]
      .map((format) => DateTime.fromFormat(startDate, format))
      .find((parsed) => parsed.isValid);

    const processingDate = getNextBusinessDay(
      sendDate ?? DateTime.now(),
      holidayDates,
    );

    return {
      kind: "standard",
      arrivalDate: addBusinessDays(processingDate, 1, holidayDates).toFormat(
        "EEEE, MMMM d",
      ),
    };
  }, [
    deliverySpeed,
    startDate,
    holidayDates,
    institution?.ach_next_cutoff,
    isDualApprovalRequired,
    isSingleExpediteEnabled,
  ]);
};

type DeliverySpeedDetailsValues = Pick<
  ACHPaymentFormType | CollectPaymentFormType,
  "amount" | "deliverySpeed"
>;

type DeliverySpeedDetailsOverrides = DeliverySpeedDetailsValues & {
  isExpeditedAvailable?: boolean;
};

export const useSelectedDeliverySpeedDetails = (
  overrideValues?: DeliverySpeedDetailsOverrides,
) => {
  const t = useLibrary("translations");
  const {
    values: { amount: formAmount, deliverySpeed: formDeliverySpeed },
  } = ACHPaymentForm.useForm();
  const amount = overrideValues?.amount ?? formAmount;
  const deliverySpeed = overrideValues?.deliverySpeed ?? formDeliverySpeed;
  const isSingleExpediteEnabled = useIsSingleExpediteEnabled();
  const isExpeditedAvailable =
    overrideValues?.isExpeditedAvailable ?? isSingleExpediteEnabled;
  const { getInstantOrSameDayFees } = useGetInstantOrSameDayFees();
  const [feeState, setFeeState] = useState<FeeQueryState>(LOADING_FEE_STATE);

  const isExpedited =
    isExpeditedAvailable &&
    (deliverySpeed === "instant" || deliverySpeed === "sameDay");

  useEffect(() => {
    let isMounted = true;

    if (!isExpedited) {
      setFeeState(LOADING_FEE_STATE);
      return undefined;
    }

    setFeeState(LOADING_FEE_STATE);
    getInstantOrSameDayFees(deliverySpeed === "instant" ? "instant" : "sameDay")
      .then((fee) => {
        if (!isMounted) return;
        setFeeState(fee ? { status: "loaded", fee } : { status: "error" });
      })
      .catch(() => {
        if (isMounted) setFeeState({ status: "error" });
      });

    return () => {
      isMounted = false;
    };
  }, [deliverySpeed, getInstantOrSameDayFees, isExpedited]);

  const speedLabel = useMemo(() => {
    if (deliverySpeed === "instant") {
      return t.getString("label-instant", {}, "Instant");
    }

    if (deliverySpeed === "sameDay") {
      return t.getString("label-same-day", {}, "Same-day ACH");
    }

    return t.getString("label-standard", {}, "Standard");
  }, [deliverySpeed, t]);

  const feeLabel = useMemo(() => {
    if (!isExpedited) {
      return t.getString("label-no-fee", {}, "No fee");
    }

    if (feeState.status === "loading") {
      return t.getString("label-loading", {}, "Loading…");
    }

    if (feeState.status === "error") {
      return t.getString(
        "fee-information-unavailable",
        {},
        "Fee information unavailable.",
      );
    }

    const fee = calculateInstantOrSameDayFee(
      amount || (0 as Cents),
      feeState.fee,
    );
    if (fee === 0) {
      return t.getString("label-no-fee", {}, "No fee");
    }

    return filters.currency(fee as Dollars, { hasDecimal: true });
  }, [amount, feeState, isExpedited, t]);

  return useObjectMemo({
    feeLabel,
    isExpedited,
    speedLabel,
  });
};

// arrival date for the given delivery speed, independent of approval/eligibility
// gating (dual approval, single_expedite) — used where the estimate must
// always be shown regardless of those flags
export const getACHArrivalEstimateDate = (
  deliverySpeed: ACHDeliverySpeed,
  startDate: string,
  achNextCutoff: number | undefined,
  holidayDates: string[],
): DateTime => {
  const now = DateTime.now();

  if (deliverySpeed === "instant") {
    return now;
  }

  if (deliverySpeed === "sameDay") {
    // once the cut-off has passed, a same-day payment settles the next
    // business day instead of arriving today
    return getIsPastACHCutoff(achNextCutoff)
      ? getNextBusinessDay(now.plus({ days: 1 }), holidayDates)
      : now;
  }

  // standard ACH arrives one business day after it is processed (the send
  // date, or the next business day if it falls on a weekend/holiday).
  // startDate may be "MM/dd/yyyy" (web) or "yyyy-MM-dd" (mobile)
  const sendDate = ["MM/dd/yyyy", "yyyy-MM-dd"]
    .map((format) => DateTime.fromFormat(startDate, format))
    .find((parsed) => parsed.isValid);

  const processingDate = getNextBusinessDay(sendDate ?? now, holidayDates);
  return addBusinessDays(processingDate, 1, holidayDates);
};

// formats an arrival date into the localized arrival estimate copy shared
// between azul and atlas — always renders, regardless of delivery speed or
// approval requirements. Callers can override the fluent id/fallback for
// platform-specific copy (e.g. atlas's shorter "Estimated by" text)
export const getACHArrivalEstimateLabel = (
  arrivalDate: DateTime,
  getString: ReactLocalization["getString"],
  fluentId: string = "ach-arrival-estimate-standard",
  fallback?: string,
): string => {
  const formattedDate = arrivalDate.toFormat("EEEE, MMMM d");

  return getString(
    fluentId,
    { arrivalDate: formattedDate },
    fallback ?? `Estimated to arrive by ${formattedDate}`,
  );
};

export const useACHArrivalEstimateLabel = (
  fluentId?: string,
  fallback?: string,
): string => {
  const institution = useInstitution();
  const holidayDates = holidays.useHolidays();
  const {
    values: { deliverySpeed, startDate },
  } = ACHPaymentForm.useForm();
  const t = useLibrary("translations");

  const arrivalDate = useMemo(
    () =>
      getACHArrivalEstimateDate(
        deliverySpeed,
        startDate,
        institution?.ach_next_cutoff,
        holidayDates,
      ),
    [deliverySpeed, startDate, institution?.ach_next_cutoff, holidayDates],
  );

  // ReactLocalization#getString relies on `this`, so it can't be passed as a
  // bare reference — wrap it to preserve the binding
  return getACHArrivalEstimateLabel(
    arrivalDate,
    (...args) => t.getString(...args),
    fluentId,
    fallback,
  );
};

type ACHReceiptSubtitleValues = Pick<
  ACHPaymentFormType | CollectPaymentFormType,
  "deliverySpeed" | "startDate"
>;

export const useACHReceiptSubtitle = (
  overrideValues?: ACHReceiptSubtitleValues,
): string => {
  const institution = useInstitution();
  const holidayDates = holidays.useHolidays();
  const {
    values: { deliverySpeed: formDeliverySpeed, startDate: formStartDate },
  } = ACHPaymentForm.useForm();
  const t = useLibrary("translations");
  const deliverySpeed = overrideValues?.deliverySpeed ?? formDeliverySpeed;
  const startDate = overrideValues?.startDate ?? formStartDate;

  return useMemo(() => {
    if (deliverySpeed === "instant") {
      return t.getString(
        "ach-payment-receipt-arrival-estimate-instant",
        {},
        "Your payment is estimated to arrive within minutes.",
      );
    }

    if (deliverySpeed === "sameDay") {
      const cutoffTime = getReadableTimeFromCutoff(
        institution?.ach_next_cutoff,
      );

      if (!cutoffTime) {
        const arrivalDate = getACHArrivalEstimateDate(
          deliverySpeed,
          startDate,
          institution?.ach_next_cutoff,
          holidayDates,
        );

        return t.getString(
          "ach-payment-receipt-arrival-estimate",
          { estimatedTime: arrivalDate.toFormat("EEEE, MMMM d") },
          `Your payment is estimated to arrive by ${arrivalDate.toFormat("EEEE, MMMM d")}.`,
        );
      }

      return t.getString(
        "ach-payment-receipt-arrival-estimate",
        { estimatedTime: cutoffTime },
        `Your payment is estimated to arrive by ${cutoffTime}.`,
      );
    }

    const arrivalDate = getACHArrivalEstimateDate(
      deliverySpeed,
      startDate,
      institution?.ach_next_cutoff,
      holidayDates,
    );

    return t.getString(
      "ach-payment-receipt-arrival-estimate",
      { estimatedTime: arrivalDate.toFormat("EEEE, MMMM d") },
      `Your payment is estimated to arrive by ${arrivalDate.toFormat("EEEE, MMMM d")}.`,
    );
  }, [deliverySpeed, holidayDates, institution?.ach_next_cutoff, startDate, t]);
};

export const useAdditionalDetails = () => {
  const institution = useInstitution();
  const sudoModeRequired = institution?.sudo_mode_required_for_ach_payments;
  const form = ACHPaymentForm.useForm();
  const {
    submitForm,
    setValidationSchema,
    validationSchema: existingSchema,
  } = form;
  const recipient = recipients.useSelectedRecipient();
  const { clearAllToasts } = useLibrary("toasts");

  useEffect(() => {
    const schema = getAchScheduledAdditionalDetailsSchema(existingSchema);
    setValidationSchema(schema);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onContinue = useCallback(
    async (next: () => void, trySudo: () => void) => {
      const { success } = await submitForm();
      if (!success) {
        return null;
      }

      clearAllToasts();

      if (!sudoModeRequired) {
        return next();
      }

      return trySudo();
    },
    [clearAllToasts, submitForm, sudoModeRequired],
  );

  return useObjectMemo({
    onContinue,
    recipient,
    form,
  });
};

export function useCalculateTotalPayments() {
  const form = ACHPaymentForm.useForm();
  const { values } = form;
  const { recurringRule } = values;

  // only calculate total payments when the repeated payment section is visible
  // and user has toggled either "on" or "after" (not "never"), or user changes start date
  const shouldCalculateTotalPayments =
    recurringRule?.isVisible &&
    (recurringRule?.ends === "on" || recurringRule?.ends === "after");

  const calculateTotalPayments = useCallback(() => {
    if (shouldCalculateTotalPayments) {
      // if user toggles "after" but there is no count, just set total payments to 0
      if (recurringRule?.ends === "after" && !recurringRule?.count) {
        return 0;
      }

      const { frequency, startDate, untilDate, count } =
        formatValuesForRecurrenceString(values);

      const calculatedTotalPayments = getTotalCountFromRecurrenceString(
        frequency,
        startDate,
        untilDate,
        count,
      );

      return calculatedTotalPayments;
    }

    return 0;
  }, [recurringRule, shouldCalculateTotalPayments, values]);

  return useObjectMemo({
    calculateTotalPayments,
  });
}

export const usePaymentDetails = ({
  shouldValidateDescription = false,
  shouldValidateInternalNote = false,
  shouldValidateAdditionalDetails = false,
  // lets a caller that's kept mounted behind another screen (e.g. invoice pay's
  // details step, still alive underneath its payment-details step for back-gesture
  // support) freeze its schema/validation instead of recomputing it - from
  // identical inputs - on every keystroke alongside whichever screen is actually
  // focused. Defaults to true so existing single-screen callers are unaffected.
  isActive = true,
}: {
  shouldValidateDescription?: boolean;
  shouldValidateInternalNote?: boolean;
  shouldValidateAdditionalDetails?: boolean;
  isActive?: boolean;
} = {}) => {
  const institution = useInstitution();
  const isBusinessUser = useIsBusinessUser();
  const achUseAvailableBalanceForScheduledPush =
    institution?.ach_use_available_balance_for_scheduled_push;
  const sudoModeRequired = institution?.sudo_mode_required_for_ach_payments;
  const form = ACHPaymentForm.useForm();
  const limitsForDisplay = limits.useLimits();
  const accounts = useAccounts();
  const isInstantDelivery = useIsInstantACHDelivery();
  const {
    submitForm,
    setValidationSchema,
    validateForm,
    validationSchema,
    values,
  } = form;
  const recipient = recipients.useSelectedRecipient();
  const { clearAllToasts } = useLibrary("toasts");
  const isOneTimePaymentForToday = getIsOneTimePaymentForToday(
    values,
    institution?.ach_next_cutoff,
  );

  const applyValidationSchema = useCallback(() => {
    const schema = getAchScheduledPaymentDetailsSchema({
      limitsForDisplay,
      values,
      isOneTimePaymentForToday,
      achUseAvailableBalanceForScheduledPush: Boolean(
        achUseAvailableBalanceForScheduledPush,
      ),
      isBusinessUser,
      accounts,
      isInstantDelivery,
      shouldValidateDescription,
      shouldValidateInternalNote,
      shouldValidateAdditionalDetails,
    });

    setValidationSchema(schema);
  }, [
    limitsForDisplay,
    values,
    isOneTimePaymentForToday,
    achUseAvailableBalanceForScheduledPush,
    isBusinessUser,
    accounts,
    isInstantDelivery,
    shouldValidateDescription,
    shouldValidateInternalNote,
    shouldValidateAdditionalDetails,
    setValidationSchema,
  ]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    applyValidationSchema();
  }, [applyValidationSchema, isActive]);

  // setValidationSchema only schedules a Provider state update, so validating
  // immediately after calling it (in applyValidationSchema above) would still run
  // against the previous schema. Re-validate here instead, once the new schema has
  // actually reached Formik, so errors/isValid (e.g. amount-limit checks) reflect it.
  useEffect(() => {
    if (!isActive) {
      return;
    }
    validateForm().catch(() => {});
  }, [validationSchema, validateForm, isActive]);

  const onContinue = useCallback(
    async (next: () => void, trySudo?: () => void) => {
      const { success } = await submitForm();

      if (!success) {
        return null;
      }

      clearAllToasts();

      // instant delivery skips the additional details step and goes straight to
      // review, so the sudo step-up that normally happens there must happen here instead
      if (isInstantDelivery && sudoModeRequired && trySudo) {
        return trySudo();
      }

      return next();
    },
    [clearAllToasts, submitForm, isInstantDelivery, sudoModeRequired],
  );

  return useObjectMemo({
    onContinue,
    applyValidationSchema,
    recipient,
    form,
  });
};

// specific to mobile, and uses mobile fluent IDs
export const useReviewDetails = () => {
  const { values } = ACHPaymentForm.useForm();
  const {
    fromAccount,
    amount,
    secCode,
    startDate,
    recurringRule,
    description,
    memo,
    entryDesc,
  } = values;
  const { send: fetchACHCompanies } = achCompanies.useFetchACHCompanies();
  const requiresACHDualApproval = useSelector(achDualApprovalRequired);
  const recipient = recipients.useSelectedRecipient();
  const account = useBAccount(fromAccount as API.AccountId);
  const t = useLibrary("translations");
  const achCompany = achCompanies.useACHCompany();
  const deliverySpeedDetails = useSelectedDeliverySpeedDetails();

  const { calculateTotalPayments } = useCalculateTotalPayments();
  const numOfPayments = calculateTotalPayments();

  useEffect(() => {
    fetchACHCompanies(`institution_account=${fromAccount}`);
  }, [fromAccount, fetchACHCompanies]);

  return useMemo(() => {
    if (!recipient || !account || !amount || !secCode) {
      return null;
    }

    type Section = {
      header: string;
      rows: Array<{ header: string; value: string }>;
    };

    const unknown = t.getString("ach-payment-review-unknown", {}, "Unknown");

    const totalPayments = calculateTotalPayments();
    const totalAmount = (totalPayments * amount) as Cents;

    const getDisplaySendDate = () => {
      return filters.americanDate(startDate);
    };

    const paymentSchedule = {
      header: t.getString(
        "ach-payment-review-payment-schedule-header",
        {},
        "Payment schedule",
      ),
      rows: [
        {
          header: t.getString(
            "ach-payment-review-start-date",
            {},
            "Start date",
          ),
          value: filters.americanDate(startDate) || "",
        },
        {
          header: t.getString("ach-payment-review-frequency", {}, "Frequency"),
          value: recurringRule?.frequency || "",
        },
        {
          header: t.getString("ach-payment-review-end-date", {}, "End date"),
          value:
            recurringRule?.ends === "on" && recurringRule?.untilDate
              ? filters.americanDate(recurringRule.untilDate)
              : "",
        },
        {
          header: `${numOfPayments} ${t.getString("ach-payment-review-payments-total", {}, "payments total")}`,
          value:
            recurringRule?.ends === "after" || recurringRule?.ends === "on"
              ? filters.currency(totalAmount)
              : "",
        },
      ].filter((row) => !!row.value),
    };

    const paymentDetails = {
      header: t.getString(
        "ach-payment-review-payment-details-header",
        {},
        "Payment details",
      ),
      rows: [
        {
          header: t.getString("ach-payment-review-from ", {}, "From"),
          value: account.getShortDescription(false),
        },
        {
          header: t.getString("ach-payment-review-memo", {}, "Memo"),
          value: description || "",
        },
        {
          header: t.getString("ach-payment-review-date", {}, "Send date"),
          value: requiresACHDualApproval
            ? t.getString("pending-table-one-time-payment", {}, "Upon approval")
            : getDisplaySendDate(),
        },
        ...(deliverySpeedDetails.isExpedited
          ? [
              {
                header: t.getString("speed-label", {}, "Speed"),
                value: deliverySpeedDetails.speedLabel,
              },
              {
                header: t.getString("label-fee", {}, "Delivery fee"),
                value: deliverySpeedDetails.feeLabel,
              },
            ]
          : []),
        {
          header: t.getString(
            "ach-payment-review-transaction-type",
            {},
            "Transaction type",
          ),
          value: t.getString(
            "ach-payment-review-sec-code",
            { secCode },
            `${secCode} payment`,
          ),
        },
        {
          header: t.getString(
            "ach-payment-review-entry-desc",
            {},
            "Entry description",
          ),
          value: entryDesc || "",
        },
        {
          header: t.getString(
            "ach-payment-review-company-name",
            {},
            "Company name",
          ),
          value: achCompany ? achCompany.company_name : unknown,
        },
        {
          header: t.getString(
            "ach-payment-review-company-id",
            {},
            "Company ID",
          ),
          value: achCompany ? achCompany.company_id : unknown,
        },
      ].filter((row) => row.value),
    };

    const internalDetails = {
      header: t.getString(
        "ach-payment-review-internal-details-header",
        {},
        "Internal details",
      ),
      rows: [
        {
          header: t.getString("ach-payment-review-note", {}, "Note"),
          value: memo || "",
        },
      ].filter((row) => row.value),
    };

    const recipientDetails = {
      header: t.getString(
        "ach-payment-review-recipient-details-header",
        {},
        "Recipient details",
      ),
      rows: [
        {
          header: t.getString("ach-payment-review-recipient", {}, "Recipient"),
          value: recipient.name,
        },
        {
          header: t.getString("ach-payment-review-bank", {}, "Bank"),
          value: recipient.ach_destination?.institution_name || unknown,
        },
        {
          header: t.getString(
            "ach-payment-review-account",
            {},
            "Account number",
          ),
          value: recipient.ach_destination?.account_number || unknown,
        },
        {
          header: t.getString(
            "ach-payment-review-routing",
            {},
            "Routing account",
          ),
          value: recipient.ach_destination?.routing_number || unknown,
        },
      ],
    };

    const sections: Array<Section> = [
      paymentDetails,
      recipientDetails,
      internalDetails,
    ];

    return {
      sections,
      paymentSchedule,
      buttonLabel: t.getString("ach-payment-review-action", {}, "Make payment"),
      amount: filters.currency(amount),
      recipient,
    };
  }, [
    account,
    achCompany,
    amount,
    description,
    deliverySpeedDetails.feeLabel,
    deliverySpeedDetails.isExpedited,
    deliverySpeedDetails.speedLabel,
    entryDesc,
    memo,
    calculateTotalPayments,
    numOfPayments,
    recipient,
    recurringRule?.ends,
    recurringRule?.frequency,
    recurringRule?.untilDate,
    requiresACHDualApproval,
    secCode,
    startDate,
    t,
  ]);
};

export const usePermissionedRecipients = () => {
  const hasManageRecipientPermission = useManageRecipientPermission();
  const permissionedRecipients = useSelector(recipients.selectSortedRecipients);

  return permissionedRecipients.filter(
    (r) => hasManageRecipientPermission || !!r.ach_destination,
  );
};

export const useOrganizationHasMultipleApprovalUsers = (
  dualApprovalType?: "ach" | "wire",
) => {
  const currentOrgUser = useUser();
  const [
    organizationHasMultipleApprovalUsers,
    setOrganizationHasMultipleApprovalUsers,
  ] = useState(false);
  const [loading, setLoading] = useState(true);
  const { org_uuid: orgUuid } = currentOrgUser as API.User;

  const { send: fetchOrgUsers } = useFetchOrganizationUsers(
    orgUuid as unknown as API.OrganizationId,
  );

  const achRequired = useSelector(achDualApprovalRequired);
  const wireRequired = useSelector(dualApprovalRequired);

  let requiresDualApproval = true;
  if (dualApprovalType === "ach") {
    requiresDualApproval = achRequired;
  } else if (dualApprovalType === "wire") {
    requiresDualApproval = wireRequired;
  }

  const isOrgUserWithEditPerms =
    currentOrgUser?.org_role &&
    OrganizationUser.ROLES_WITH_EDIT_PERMISSION.includes(
      currentOrgUser.org_role,
    );

  useEffect(() => {
    if (!currentOrgUser) {
      setLoading(false);
      return;
    }

    // if dual approval is required, and user is admin/account holder role,
    // then determine if there are multiple org users with dual approval permissions
    if (requiresDualApproval && isOrgUserWithEditPerms) {
      setLoading(true);
      fetchOrgUsers({
        onData: ({ organization_users: orgUsers }) => {
          const hasMultipleApprovalUsers =
            Organization.getOrganizationHasMultipleApprovalUsers(
              orgUsers as API.OrganizationUser[],
            );
          setOrganizationHasMultipleApprovalUsers(hasMultipleApprovalUsers);
          setLoading(false);
        },
        onError: () => {
          setOrganizationHasMultipleApprovalUsers(false);
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useObjectMemo({
    loading,
    requiresDualApproval,
    organizationHasMultipleApprovalUsers,
    isOrgUserWithEditPerms,
  });
};

export const useShowUserNeedsApprovalAlert = (
  dualApprovalType: "ach" | "wire",
) => {
  const {
    organizationHasMultipleApprovalUsers,
    isOrgUserWithEditPerms,
    loading,
    requiresDualApproval,
  } = useOrganizationHasMultipleApprovalUsers(dualApprovalType);

  const showAlert =
    !loading &&
    requiresDualApproval &&
    !organizationHasMultipleApprovalUsers &&
    isOrgUserWithEditPerms;

  return useObjectMemo({
    showAlert,
  });
};

// fednow_send being enabled only means the org can send instant - the recipient's
// bank still needs to be checked for receiving eligibility before the option is offered
export const useInstantEligibility = (
  featureEnabled: (key: string) => boolean,
  routingNumber: string | undefined,
) => {
  const { checkReceiveInstantEligibility } =
    useCheckReceiveInstantEligibility();
  // checkReceiveInstantEligibility's identity is tied to its own request's
  // lifecycle (it's built on top of useRequestWithFeedback's send), so putting
  // it directly in the effect deps below would re-trigger a fresh request every
  // time the in-flight one settles, forever - track it in a ref instead
  const checkReceiveInstantEligibilityRef = useReferenceTo(
    checkReceiveInstantEligibility,
  );
  const isFednowEnabled = featureEnabled("fednow_send");
  // defaults to ineligible until the routing number is confirmed, rather than risk
  // a member going through the whole instant flow only to be rejected server-side
  // by validate_unverified_recipient
  const [isInstantEligible, setIsInstantEligible] = useState(false);
  // tracks whether the check below is in flight, so callers can wait for the real
  // answer instead of acting on the ineligible-by-default state above
  const [isCheckingInstantEligibility, setIsCheckingInstantEligibility] =
    useState(() => Boolean(isFednowEnabled && routingNumber));

  useEffect(() => {
    if (!isFednowEnabled || !routingNumber) {
      setIsInstantEligible(false);
      setIsCheckingInstantEligibility(false);
      return undefined;
    }

    let isMounted = true;
    setIsInstantEligible(false);
    setIsCheckingInstantEligibility(true);
    checkReceiveInstantEligibilityRef
      .current(routingNumber)
      .then((eligible) => {
        if (isMounted) {
          setIsInstantEligible(eligible);
        }
      })
      .catch(() => {
        if (isMounted) {
          setIsInstantEligible(false);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsCheckingInstantEligibility(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [checkReceiveInstantEligibilityRef, isFednowEnabled, routingNumber]);

  return useObjectMemo({ isInstantEligible, isCheckingInstantEligibility });
};

export const useDeliverySpeedFees = (
  sameDayAvailable: boolean,
  instantAvailable: boolean,
) => {
  const { getInstantOrSameDayFees } = useGetInstantOrSameDayFees();
  // same reasoning as useInstantEligibility above: getInstantOrSameDayFees's
  // identity shifts with its own request lifecycle, so it can't be a direct dep
  const getInstantOrSameDayFeesRef = useReferenceTo(getInstantOrSameDayFees);
  const [feeByType, setFeeByType] = useState<{
    instant: API.InstantFeesResponse | null;
    sameDay: API.InstantFeesResponse | null;
  }>({ instant: null, sameDay: null });

  useEffect(() => {
    let isMounted = true;

    const loadFees = async () => {
      const [sameDay, instant] = await Promise.all([
        sameDayAvailable
          ? getInstantOrSameDayFeesRef.current("sameDay").catch(() => null)
          : Promise.resolve(null),
        instantAvailable
          ? getInstantOrSameDayFeesRef.current("instant").catch(() => null)
          : Promise.resolve(null),
      ]);

      if (!isMounted) {
        return;
      }

      setFeeByType({ instant, sameDay });
    };

    loadFees();

    return () => {
      isMounted = false;
    };
  }, [getInstantOrSameDayFeesRef, instantAvailable, sameDayAvailable]);

  return feeByType;
};

// computes the recommended ACH delivery speed for an invoice due date and, once
// eligibility has resolved, applies it (or the first available speed) unless the
// member already has a valid manual selection in place - a single "recommend +
// apply" concern rather than splitting it across a hook and a caller-side effect
export const useRecommendedDeliverySpeed = ({
  recommendationDate,
  holidayDates,
  isPastCutoff,
  sameDayAvailable,
  instantAvailable,
  deliverySpeed,
  deliverySpeedManuallySelected,
  isCheckingEligibility,
  setDeliverySpeed,
}: {
  recommendationDate: string;
  holidayDates: string[];
  isPastCutoff: boolean;
  sameDayAvailable: boolean;
  instantAvailable: boolean;
  deliverySpeed: ACHDeliverySpeed;
  deliverySpeedManuallySelected: boolean | undefined;
  isCheckingEligibility: boolean;
  setDeliverySpeed: (speed: ACHDeliverySpeed) => void;
}) => {
  const computedRecommendedSpeed = useMemo(
    (): InvoicePayRecommendedSpeed =>
      getRecommendedInvoicePaySpeed({
        dueDate: recommendationDate,
        holidayDates,
        isPastCutoff,
        hasSameDayOption: sameDayAvailable,
        hasInstantOption: instantAvailable,
      }) ?? "standard",
    [
      holidayDates,
      instantAvailable,
      isPastCutoff,
      recommendationDate,
      sameDayAvailable,
    ],
  );

  // badge-facing copy of the recommendation: once the member makes a manual
  // choice it freezes rather than continuing to follow further recalculations
  const [recommendedSpeed, setRecommendedSpeed] =
    useState<InvoicePayRecommendedSpeed>(computedRecommendedSpeed);
  useEffect(() => {
    if (!deliverySpeedManuallySelected) {
      setRecommendedSpeed(computedRecommendedSpeed);
    }
  }, [computedRecommendedSpeed, deliverySpeedManuallySelected]);

  useEffect(() => {
    // wait for the real eligibility result rather than locking in a fallback
    // (and marking it "manually selected") based on the ineligible-by-default state
    if (isCheckingEligibility) {
      return;
    }

    const availableSpeeds: ACHDeliverySpeed[] = [
      "standard",
      ...(sameDayAvailable ? (["sameDay"] as const) : []),
      ...(instantAvailable ? (["instant"] as const) : []),
    ];

    if (
      deliverySpeedManuallySelected &&
      availableSpeeds.includes(deliverySpeed)
    ) {
      return;
    }

    // uses computedRecommendedSpeed (not the badge-facing, one-render-behind
    // `recommendedSpeed` state) so this can't act on a stale recommendation from
    // before instant eligibility resolved
    const nextSpeed = availableSpeeds.includes(computedRecommendedSpeed)
      ? computedRecommendedSpeed
      : availableSpeeds[0];

    if (nextSpeed && deliverySpeed !== nextSpeed) {
      setDeliverySpeed(nextSpeed);
    }
  }, [
    computedRecommendedSpeed,
    deliverySpeed,
    deliverySpeedManuallySelected,
    instantAvailable,
    isCheckingEligibility,
    sameDayAvailable,
    setDeliverySpeed,
  ]);

  return useObjectMemo({ recommendedSpeed });
};

// instant and same-day submit against today regardless of the date shown on the
// caller's screen - FedNow has no scheduling field, and a same-day payment with a
// future startDate instead fails backend validation - so pin it to today whenever
// an expedited speed is selected. switching back to standard can leave that pinned
// date in place after cutoff, when it's now earlier than the cutoff-derived
// minimum, so restore the minimum in that case
export const usePinnedACHStartDate = ({
  deliverySpeed,
  startDate,
  achNextCutoff,
  setFieldValue,
}: {
  deliverySpeed: ACHDeliverySpeed;
  startDate: string;
  achNextCutoff: number | undefined;
  setFieldValue: (
    field: "startDate",
    value: string,
    shouldValidate: boolean,
  ) => void;
}) => {
  // the most recent startDate seen while NOT expedited - i.e. whatever the member's
  // own chosen (or extracted) date was right before an expedited speed pinned it to
  // today. Mutated directly during render (matches the useReferenceTo pattern used
  // elsewhere in this file) rather than via effect, so it's never a render behind;
  // deliberately left untouched while expedited, so it keeps pointing at that prior
  // date for as long as the pin holds, for the "Changed from X" messaging below
  const lastUnpinnedDateRef = useRef(startDate);
  if (!isExpeditedACHDeliverySpeed(deliverySpeed)) {
    lastUnpinnedDateRef.current = startDate;
  }

  // computed synchronously during render (rather than via effect + setState) so
  // the displayed date can never flash the pre-pin value for a render before
  // catching up
  const effectiveStartDate = useMemo(() => {
    if (isExpeditedACHDeliverySpeed(deliverySpeed)) {
      return getStartDate();
    }

    const minDate = getStartDate(achNextCutoff);
    const parsedStartDate = parseACHStartDate(startDate);
    // getStartDate always formats as MM/dd/yyyy, unlike startDate above
    const parsedMinDate = parse(minDate, "MM/dd/yyyy", new Date());
    return Number.isNaN(parsedStartDate.getTime()) ||
      isBefore(parsedStartDate, parsedMinDate)
      ? minDate
      : startDate;
  }, [achNextCutoff, deliverySpeed, startDate]);

  // persists the computed date into the form (read at submission time); the sync
  // itself is never visible since the render above already shows effectiveStartDate
  // regardless of whether this has committed yet
  useEffect(() => {
    if (startDate !== effectiveStartDate) {
      setFieldValue("startDate", effectiveStartDate, false);
    }
  }, [effectiveStartDate, setFieldValue, startDate]);

  // surfaces what the date actually changed from, for copy like "Changed from Oct 3
  // for Instant" - undefined whenever there's nothing to report (standard speed, or
  // the member already had today selected before the expedited pin ever applied)
  const pinnedFromDate = useMemo(() => {
    if (!isExpeditedACHDeliverySpeed(deliverySpeed)) {
      return undefined;
    }

    const normalizedPriorDate = formatDate(
      parseACHStartDate(lastUnpinnedDateRef.current),
      "MM/dd/yyyy",
    );
    return normalizedPriorDate !== effectiveStartDate
      ? lastUnpinnedDateRef.current
      : undefined;
  }, [deliverySpeed, effectiveStartDate]);

  return useObjectMemo({ effectiveStartDate, pinnedFromDate });
};
